import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { reduxForm, getFormValues, Field, isInvalid, change as changeFormValue } from 'redux-form';
import cn from 'classnames';
import { Modal, Button, ReduxControls } from 'rhombus-react';
import { AUDIO_GENERATE } from "forms/list";
import { noop } from 'utils/common';
import { required } from 'widgets/forms/validators/validators';
import { allErrorsSelector } from 'widgets/forms/validators/Social';
import { creativeNameAllowedChars, CREATIVE_FILENAME_MAX_LENGTH, creativeFilenameMaxLength } from 'widgets/forms/validators/Creatives';
import { assetName } from 'widgets/forms/validators/Assets';
import { loadVoiceSettings, loadVoices, loadSoundSettings, loadSoundMoods, loadSounds, createAudioVersions } from 'reducers/GenAI';
import AudioButton from './AudioButton';
import Card from './Card';
import Select from './Select';

// eslint-disable-next-line no-magic-numbers
const CREATIVE_LENGTH_OPTIONS = [10, 15, 30];
const CUSTOM_DURATION_VALUE = 'custom';
const DURATION_LIMIT = { MIN: 5, MAX: 60 };
const VOICE_TYPES = { DISCOVER: 'discover', SELECT: 'select' };
const VOICE_CHARS_PER_SECOND_AVG = 14.37;
const VOICE_SPEED_THRESHOLD = 0.2;
const SOUND_TYPES = { DISCOVER: 'discover', SELECT: 'select', NO: '' };
const EMPTY_VALUE = '';
const DEFAULT_SELECT_OPTIONS = {
	tone: { value: EMPTY_VALUE, label: 'All Tones' },
	gender: { value: EMPTY_VALUE, label: 'All Genders' },
	language: { value: EMPTY_VALUE, label: 'All Languages' },
	accent: { value: EMPTY_VALUE, label: 'All Accents' },
	tag: { value: EMPTY_VALUE, label: 'All Styles' },
	age: { value: EMPTY_VALUE, label: 'All Age Brackets' },
	mood: { value: EMPTY_VALUE, label: 'All Moods' },
	genre: { value: EMPTY_VALUE, label: 'All Genres' },
	instrument: { value: EMPTY_VALUE, label: 'All Instruments' },
};
const RECOMMENDED_OPTION = { value: EMPTY_VALUE, label: 'Recommended' };

const getDefaultOption = (type, name) => {
	return type === 'discover' ? RECOMMENDED_OPTION : DEFAULT_SELECT_OPTIONS[name];
};
const addDefaultOption = (type, name, options) => ([getDefaultOption(type, name), ...options]);

const validateCustomDuration = (val) => {
	if (Number(val) < DURATION_LIMIT.MIN || Number(val) > DURATION_LIMIT.MAX) {
		return 'The Creative Length must be between 5 and 60 seconds.';
	}
};

export function AudioAdSettings(props){
	const {
		commonModalProps,
		Breadcrumb,
		formName,
		setPreviewVersions,
	} = props;
	const { libraryId, closeModal } = commonModalProps;

	const values = useSelector(getFormValues(formName));
	const messages = useSelector(allErrorsSelector(formName));
	const invalid = useSelector(isInvalid(formName));
	const dispatch = useDispatch();
	const [voiceSettings, setVoiceSettings] = useState({});
	const [soundMoods, setSoundMoods] = useState(undefined);
	const [soundSettings, setSoundSettings] = useState({});
	const [isLoadingVersions, setIsLoadingVersions] = useState(false);

	// default values for voice settings
	useEffect(() => {
		['tone', 'gender', 'language', 'accent', 'tag', 'age'].forEach((setting) => {
			if (!values[setting]?.value) {
				dispatch(changeFormValue(formName, setting, getDefaultOption(values.voiceType, setting)));
			}
		});
	}, [values.voiceType]);

	useEffect(() => {
		dispatch(loadVoiceSettings()).then((resp) => {
			const data = resp.payload?.data;
			if (resp.success && data) {
				setVoiceSettings(['tone', 'gender', 'language', 'accent', 'tags', 'age'].reduce((acc, setting) => {
					acc[setting] = data[setting];
					return acc;
				}, {}));
			}
		});
		dispatch(loadSoundMoods()).then((resp) => {
			const data = resp.payload?.data;
			if (resp.success && data) {
				setSoundMoods(data);
			}
		});
	}, []);

	useEffect(() => {
		if ((values.soundType === SOUND_TYPES.SELECT) && !Object.keys(soundSettings).length) {
			dispatch(loadSoundSettings()).then((resp) => {
				const data = resp.payload?.data;
				if (resp.success && data) {
					setSoundSettings({
						moods: data.moods,
						genres: data.genres,
						instruments: data.instruments,
					});
				}
			});
		}
	}, [values.soundType, soundSettings]);

	const tone = (
		<Field
			component={ReduxControls.Select}
			label="Tone of Voice"
			name="tone"
			options={addDefaultOption(values.voiceType, 'tone', voiceSettings.tone || [])}
			isClearable={false}
			disabled={voiceSettings.tone === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const gender = (
		<Field
			component={ReduxControls.Select}
			label="Gender"
			name="gender"
			options={addDefaultOption(values.voiceType, 'gender', voiceSettings.gender || [])}
			isClearable={false}
			disabled={voiceSettings.gender === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const language = (
		<Field
			component={ReduxControls.Select}
			label="Language"
			name="language"
			options={addDefaultOption(values.voiceType, 'language', voiceSettings.language || [])}
			isClearable={false}
			disabled={voiceSettings.language === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const accent = (
		<Field
			component={ReduxControls.Select}
			label="Accent"
			name="accent"
			options={addDefaultOption(values.voiceType, 'accent', voiceSettings.accent || [])}
			isClearable={false}
			disabled={voiceSettings.accent === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const tag = (
		<Field
			component={ReduxControls.Select}
			label="Style"
			name="tag"
			options={addDefaultOption(values.voiceType, 'tag', voiceSettings.tags || [])}
			isClearable={false}
			disabled={voiceSettings.tags === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const age = (
		<Field
			component={ReduxControls.Select}
			label="Age Bracket"
			name="age"
			options={addDefaultOption(values.voiceType, 'age', voiceSettings.age || [])}
			isClearable={false}
			disabled={voiceSettings.age === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const voice = (
		<Select
			label="Voice"
			name="voice"
			formName={formName}
			shouldLoadOptions={values.voiceType === VOICE_TYPES.SELECT}
			dependenciesMap={{ gender: 'gender', language: 'language', accent: 'accent', age: 'age', tag: 'tags' }}
			loadFn={loadVoices}
			responseProp="voices"
		/>
	);
	const voiceAudio = <AudioButton src={values.voice?.value?.audioSample} />;
	const voiceControls = {
		[VOICE_TYPES.DISCOVER]: [
			{ id: 'discover', controls: [{ id: 'tone', control: tone }, { id: 'gender', control: gender }, { id: 'language', control: language }, { id: 'accent', control: accent }]},
		],
		[VOICE_TYPES.SELECT]: [
			{ id: 'select1', controls: [{ id: 'language', control: language }, { id: 'accent', control: accent }]},
			{ id: 'select2', controls: [{ id: 'tag', control: tag }, { id: 'gender', control: gender }, { id: 'age', control: age }]},
			{ id: 'select3', controls: [{ id: 'voice', control: voice }, { id: 'voiceAudio', control: voiceAudio }]},
		],
	};

	const creativeLength = values.creativeLength?.value === CUSTOM_DURATION_VALUE ? values.creativeLengthCustom : values.creativeLength?.value;

	const scriptData = useMemo(() => {
		if (!creativeLength || !values.scriptText) {
			return null;
		}
		const words = values.scriptText.trim().split(/\s+/).length;
		const charsPerSecond = values.voice?.charsPerSecond || VOICE_CHARS_PER_SECOND_AVG;
		const duration = values.scriptText.length / charsPerSecond;
		const data = {
			words: `${words} words - ${duration.toFixed(1)}/${creativeLength}`,
			msg: `Within ${creativeLength}s`,
			hasError: false,
		};
		if (creativeLength < duration * (1 - VOICE_SPEED_THRESHOLD)) {
			data.msg = `Too long for ${creativeLength}s`;
			data.hasError = true;
		} else if (creativeLength > duration * (1 + VOICE_SPEED_THRESHOLD)) {
			data.msg = `Too short for ${creativeLength}s`;
			data.hasError = true;
		}
		return data;
	}, [creativeLength, values.scriptText, values.voice]);

	const scriptLabel = useMemo(() => {
		if (!scriptData) {
			return null;
		}
		return (
			<span className="script-labels">
				<span className="script-label">
					{scriptData.words}
				</span>
				<span className={cn('script-label', { 'script-label-success': !scriptData.hasError, 'script-label-error': scriptData.hasError })}>
					{scriptData.msg}
				</span>
			</span>
		);
	}, [scriptData]);

	const discoverMood = (
		<Field
			component={ReduxControls.Select}
			label="Mood"
			name="discoverMood"
			options={addDefaultOption(values.soundType, 'discoverMood', soundMoods || [])}
			isClearable={false}
			disabled={soundMoods === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const mood = (
		<Field
			component={ReduxControls.Select}
			label="Mood"
			name="mood"
			options={addDefaultOption(values.soundType, 'mood', soundSettings.moods || [])}
			isClearable={false}
			disabled={soundSettings.moods === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const genre = (
		<Field
			component={ReduxControls.Select}
			label="Genre"
			name="genre"
			options={addDefaultOption(values.soundType, 'genre', soundSettings.genres || [])}
			isClearable={false}
			disabled={soundSettings.genres === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const instrument = (
		<Field
			component={ReduxControls.Select}
			label="Instruments"
			name="instrument"
			options={addDefaultOption(values.soundType, 'instrument', soundSettings.instruments || [])}
			isClearable={false}
			disabled={soundSettings.instruments === undefined}
			onMenuScrollToBottom={noop}
		/>
	);
	const sound = (
		<Select
			label="Background Sound"
			name="sound"
			formName={formName}
			shouldLoadOptions={values.soundType === SOUND_TYPES.SELECT}
			dependenciesMap={{ mood: 'moods', genre: 'genres', instrument: 'instruments' }}
			loadFn={loadSounds}
			responseProp="sounds"
		/>
	);
	const soundAudio = <AudioButton src={values.sound?.value?.audioSample} />;
	const soundControls = {
		[SOUND_TYPES.DISCOVER]: [
			{ id: 'discover', controls: [{ id: 'discoverMood', control: discoverMood }]},
		],
		[SOUND_TYPES.SELECT]: [
			{ id: 'select1', controls: [{ id: 'mood', control: mood }, { id: 'genre', control: genre }, { id: 'instrument', control: instrument }]},
			{ id: 'select2', controls: [{ id: 'sound', control: sound }, { id: 'soundAudio', control: soundAudio }]},
		],
		[SOUND_TYPES.NO]: [],
	};

	const onRadioChange = name => (e) => {
		dispatch(changeFormValue(formName, name, e.target.value));
	};

	const creativeLengthOptions = [
		...CREATIVE_LENGTH_OPTIONS.map(value => ({ value, label: `${value} seconds` })),
		{ value: CUSTOM_DURATION_VALUE, label: 'Custom duration' },
	];

	function renderContent() {
		return (
			<>
				{Breadcrumb}
				<div className="c-modal-body">
					<div className="form-field">
						<Field
							component={ReduxControls.Input}
							required
							label="Creative Name"
							name="creativeName"
							placeholder="Enter Creative Name..."
							className={messages.length ? "error" : ""}
							validate={[required, creativeFilenameMaxLength, creativeNameAllowedChars, assetName]}
							showCharCount
							charMax={CREATIVE_FILENAME_MAX_LENGTH}
						/>
					</div>
					<div className="form-field length-fields">
						<Field
							component={ReduxControls.Select}
							required
							label="Creative Length"
							name="creativeLength"
							options={creativeLengthOptions}
							className={`length-select ${messages.length ? "error" : ""}`}
							isClearable={false}
							onMenuScrollToBottom={noop}
						/>
						{values.creativeLength?.value === CUSTOM_DURATION_VALUE && (
							<>
								<Field
									component={ReduxControls.Input}
									type="number"
									required
									label="&nbsp;"
									name="creativeLengthCustom"
									className={`length-custom ${messages.length ? "error" : ""}`}
									validate={[required, validateCustomDuration]}
								/>
								<span>seconds</span>
							</>
						)}
					</div>
					<div className="form-field">
						{scriptLabel}
						<Field component={ReduxControls.Input}
							   type="textarea"
							   required
							   label="Script Text"
							   name="scriptText"
							   placeholder="Write or paste your script here."
							   className={messages.length ? "error" : ""}
							   validate={[required]}
						/>
					</div>
					<Card
						header="Voice Settings"
						hasIcon
					>
						<div className="card-content-row">
							<div className="radio-inline">
								<ReduxControls.Checkbox
									type="radio"
									name="voiceType"
									value={VOICE_TYPES.DISCOVER}
									label="Discover"
									checked={values.voiceType === VOICE_TYPES.DISCOVER}
									onChange={onRadioChange('voiceType')}
								/>
								<ReduxControls.Checkbox
									type="radio"
									name="voiceType"
									value={VOICE_TYPES.SELECT}
									label="Select from the library"
									checked={values.voiceType === VOICE_TYPES.SELECT}
									onChange={onRadioChange('voiceType')}
								/>
							</div>
						</div>
						{voiceControls[values.voiceType].map(row => (
							<div key={row.id} className="card-content-row">
								{row.controls.map(item => (<React.Fragment key={item.id}>{item.control}</React.Fragment>))}
							</div>
						))}
					</Card>
					<Card
						// className="card"
						header="Background Sound Settings"
						hasIcon
					>
						<div className="card-content-row">
							<div className="radio-inline">
								<ReduxControls.Checkbox
									type="radio"
									name="soundType"
									value={SOUND_TYPES.DISCOVER}
									label="Discover"
									checked={values.soundType === SOUND_TYPES.DISCOVER}
									onChange={onRadioChange('soundType')}
								/>
								<ReduxControls.Checkbox
									type="radio"
									name="soundType"
									value={SOUND_TYPES.SELECT}
									label="Select from the library"
									checked={values.soundType === SOUND_TYPES.SELECT}
									onChange={onRadioChange('soundType')}
								/>
								<ReduxControls.Checkbox
									type="radio"
									name="soundType"
									value={SOUND_TYPES.NO}
									label="No Sound"
									checked={values.soundType === SOUND_TYPES.NO}
									onChange={onRadioChange('soundType')}
								/>
							</div>
						</div>
						{soundControls[values.soundType].map(row => (
							<div key={row.id} className="card-content-row">
								{row.controls.map(item => (<React.Fragment key={item.id}>{item.control}</React.Fragment>))}
							</div>
						))}
					</Card>
				</div>
			</>
		);
	}

	function previewAudio() {
		const hasVoiceSelected = (values.voiceType === VOICE_TYPES.SELECT) && values.voice;
		const hasSoundSelected = (values.soundType === SOUND_TYPES.SELECT) && values.sound;
		const hasVersion = hasVoiceSelected && (hasSoundSelected || values.soundType === SOUND_TYPES.NO);
		if (hasVersion) {
			const version = {
				scriptText: values.scriptText,
				voice: values.voice.value,
			};
			if (hasSoundSelected) {
				version.backgroundSound = values.sound.value;
			}
			setPreviewVersions([version]);
		} else {
			setIsLoadingVersions(true);
			let voiceParams;
			if (hasVoiceSelected) {
				voiceParams = { alias: values.voice.value.alias };
			} else {
				voiceParams = ['tone', 'gender', 'language', 'accent'].reduce((acc, param) => {
					if (values[param]?.value) {
						acc[param] = values[param].value;
					}
					return acc;
				}, {});
			}
			const backgroundSoundParams = {};
			if (hasSoundSelected) {
				backgroundSoundParams.alias = values.sound.value.alias;
			} else if (values.discoverMood) {
				backgroundSoundParams.mood = values.discoverMood.value;
			}
			const data = {
				creativeLibraryId: libraryId,
				text: values.scriptText,
				audioDuration: creativeLength,
				voice: voiceParams,
				backgroundSound: backgroundSoundParams,
			};
			dispatch(createAudioVersions(data))
				.then((resp) => {
					const versions = resp.payload?.data?.versions;
					if (resp.success && versions) {
						setPreviewVersions(versions.map(v => ({ ...v, scriptText: values.scriptText })));
					}
				})
				.finally(() => {
					setIsLoadingVersions(false);
				});
		}
	}

	function renderFooter() {
		return (
			<>
				<Button theme="default" onClick={closeModal}>Cancel</Button>
				<Button
					theme="blue"
					disabled={invalid || scriptData?.hasError || isLoadingVersions}
					onClick={previewAudio}
					icon={isLoadingVersions ? 'fa-regular fa-circle-notch circle-spin' : null}
				>Preview Audio</Button>
			</>
		);
	}

	return <Modal {...commonModalProps} renderContent={renderContent} renderFooter={renderFooter} />;
}

AudioAdSettings.displayName = "AudioAdSettings";

const formWrapper = reduxForm({
	form: AUDIO_GENERATE,
	initialValues: {
		creativeLength: { value: 15, label: '15 seconds' },
		voiceType: VOICE_TYPES.DISCOVER,
		soundType: SOUND_TYPES.DISCOVER,
		discoverMood: getDefaultOption(SOUND_TYPES.DISCOVER, 'discoverMood'),
		mood: getDefaultOption(SOUND_TYPES.SELECT, 'mood'),
		genre: getDefaultOption(SOUND_TYPES.SELECT, 'genre'),
		instrument: getDefaultOption(SOUND_TYPES.SELECT, 'instrument'),
	},
	destroyOnUnmount: false,
});

export default formWrapper(AudioAdSettings);
